import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

/**
 * Shows a small "Install app" affordance.
 * - Chrome/Edge/Android: captures `beforeinstallprompt`, renders a button that
 *   triggers the native install dialog.
 * - iOS Safari: no programmatic prompt exists; we show a one-time tip explaining
 *   the Share → "Add to Home Screen" flow.
 * - Hidden once the app is already running in standalone mode, or once dismissed.
 */
export default function InstallPrompt({ variant = "sidebar" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIosTip, setShowIosTip] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) { setInstalled(true); return; }

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;
  if (typeof window === "undefined") return null;
  if (window.localStorage?.getItem("es-install-dismissed") === "1" && !deferred) return null;

  const ua = window.navigator.userAgent || "";
  const isIos = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
  const inStandalone = window.navigator.standalone === true;

  // Native install (Chrome / Edge / Android)
  if (deferred) {
    return (
      <button
        data-testid="install-app-btn"
        onClick={async () => { await deferred.prompt(); const choice = await deferred.userChoice; if (choice?.outcome) setDeferred(null); }}
        className={
          variant === "sidebar"
            ? "w-full flex items-center justify-center gap-2 h-9 mt-2 text-sm font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 rounded-md transition-colors ring-1 ring-brand-200"
            : "es-btn-secondary"
        }
        title="Install Edenstone to your home screen"
      >
        <Download className="w-4 h-4" /> Install app
      </button>
    );
  }

  // iOS Safari: explain the Share → Add to Home Screen flow
  if (isIos && !inStandalone) {
    return (
      <>
        <button
          data-testid="install-app-ios-btn"
          onClick={() => setShowIosTip(true)}
          className={
            variant === "sidebar"
              ? "w-full flex items-center justify-center gap-2 h-9 mt-2 text-sm font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 rounded-md transition-colors ring-1 ring-brand-200"
              : "es-btn-secondary"
          }
        >
          <Download className="w-4 h-4" /> Add to home screen
        </button>
        {showIosTip && (
          <div className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowIosTip(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <h3 className="font-display text-lg font-bold text-zinc-900">Install Edenstone</h3>
                <button onClick={() => setShowIosTip(false)} className="text-zinc-400 hover:text-zinc-900"><X className="w-5 h-5" /></button>
              </div>
              <ol className="space-y-3 text-sm text-zinc-700">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-brand-700 flex-shrink-0">1.</span>
                  <span>Tap the <strong>Share</strong> button <Share className="w-4 h-4 inline mb-0.5" /> at the bottom of Safari.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-brand-700 flex-shrink-0">2.</span>
                  <span>Scroll down and tap <strong>"Add to Home Screen"</strong>.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-brand-700 flex-shrink-0">3.</span>
                  <span>Tap <strong>Add</strong>. The Edenstone icon will appear on your home screen.</span>
                </li>
              </ol>
              <button
                onClick={() => { window.localStorage?.setItem("es-install-dismissed", "1"); setShowIosTip(false); }}
                className="w-full mt-5 es-btn-secondary justify-center"
              >Don't show again</button>
            </div>
          </div>
        )}
      </>
    );
  }

  return null;
}
